(function () {
  'use strict';

  var STORAGE_KEY = 'four-nordic-rfq-v1';
  var MAX_ITEMS = 50;
  var memoryItems = [];
  var storageAvailable = true;

  function trackEvent(eventName, parameters) {
    var payload = Object.assign({ event: eventName }, parameters || {});
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    document.dispatchEvent(new CustomEvent('four-nordic:analytics', { detail: payload }));
  }

  function cleanText(value, maxLength) {
    return String(value || '').replace(/\s+/g, ' ').trim().slice(0, maxLength || 300);
  }

  function cleanQuantity(value) {
    var quantity = parseInt(value, 10);
    return Math.min(Math.max(isNaN(quantity) ? 1 : quantity, 1), 9999);
  }

  function cleanCondition(value) {
    return ['any', 'new', 'spare', 'refurbished'].indexOf(value) !== -1 ? value : 'any';
  }

  function cleanUrl(value) {
    if (!value) return '';

    try {
      var url = new URL(value, window.location.origin);
      return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : '';
    } catch (error) {
      return '';
    }
  }

  function normalizeItem(item) {
    if (!item || typeof item !== 'object') return null;

    var type = item.type === 'manual' ? 'manual' : 'product';
    var reference = cleanText(item.reference || item.sku || item.title, 200);
    var productId = cleanText(item.productId, 50);
    var variantId = cleanText(item.variantId, 50);

    if (!reference && type === 'manual') return null;
    if (!productId && !variantId && type === 'product') return null;

    return {
      id: cleanText(item.id, 260) || (type === 'manual' ? 'manual:' + reference.toLowerCase() : 'product:' + productId + ':' + variantId),
      type: type,
      productId: productId,
      variantId: variantId,
      title: cleanText(item.title, 250),
      variantTitle: cleanText(item.variantTitle, 200),
      sku: cleanText(item.sku, 200),
      reference: reference,
      url: cleanUrl(item.url),
      quantity: cleanQuantity(item.quantity),
      condition: cleanCondition(item.condition)
    };
  }

  function readItems() {
    var storedItems;

    if (!storageAvailable) return memoryItems.slice();

    try {
      storedItems = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
    } catch (error) {
      storageAvailable = false;
      storedItems = memoryItems;
    }

    if (!Array.isArray(storedItems)) storedItems = [];

    return storedItems.map(normalizeItem).filter(Boolean).slice(0, MAX_ITEMS);
  }

  function saveItems(items) {
    memoryItems = items.map(normalizeItem).filter(Boolean).slice(0, MAX_ITEMS);

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryItems));
    } catch (error) {
      storageAvailable = false;
      // The in-memory fallback still keeps the RFQ functional on the current page.
    }

    renderInterface(memoryItems);
    document.dispatchEvent(new CustomEvent('four-nordic:quote-updated', {
      detail: { items: memoryItems.slice() }
    }));
  }

  function conditionLabels(builder) {
    return {
      any: builder.dataset.anyConditionLabel,
      new: builder.dataset.newConditionLabel,
      spare: builder.dataset.spareConditionLabel,
      refurbished: builder.dataset.refurbishedConditionLabel
    };
  }

  function conditionValueForEmail(value) {
    return {
      any: 'Any acceptable',
      new: 'NEW',
      spare: 'SPARE',
      refurbished: 'Refurbished'
    }[cleanCondition(value)];
  }

  function makeElement(tagName, className, textValue) {
    var element = document.createElement(tagName);
    if (className) element.className = className;
    if (typeof textValue === 'string') element.textContent = textValue;
    return element;
  }

  function appendField(itemElement, item, builder, fieldName, index) {
    var wrapper = makeElement('div', 'rfq-item__field rfq-item__field--' + fieldName);
    var fieldId = 'rfq-item-' + index + '-' + fieldName;
    var label = makeElement('label', '', fieldName === 'quantity' ? builder.dataset.quantityLabel : builder.dataset.conditionLabel);
    label.setAttribute('for', fieldId);
    wrapper.appendChild(label);

    if (fieldName === 'quantity') {
      var input = makeElement('input', 'form__field form__field--text');
      input.id = fieldId;
      input.type = 'number';
      input.min = '1';
      input.max = '9999';
      input.inputMode = 'numeric';
      input.value = item.quantity;
      input.setAttribute('data-rfq-item-quantity', '');
      wrapper.appendChild(input);
    } else {
      var selectWrapper = makeElement('div', 'select-wrapper select-wrapper--primary');
      var select = makeElement('select');
      var labels = conditionLabels(builder);
      select.id = fieldId;
      select.setAttribute('data-rfq-item-condition', '');

      ['any', 'new', 'spare', 'refurbished'].forEach(function (condition) {
        var option = makeElement('option', '', labels[condition]);
        option.value = condition;
        option.selected = item.condition === condition;
        select.appendChild(option);
      });

      var arrow = makeElement('span', 'rfq-item__select-arrow', '⌄');
      arrow.setAttribute('aria-hidden', 'true');
      selectWrapper.appendChild(arrow);
      selectWrapper.appendChild(select);
      wrapper.appendChild(selectWrapper);
    }

    itemElement.appendChild(wrapper);
  }

  function renderItem(item, builder, index) {
    var itemElement = makeElement('article', 'rfq-item');
    itemElement.dataset.rfqItemId = item.id;

    var details = makeElement('div', 'rfq-item__details');
    var title = makeElement('strong', 'rfq-item__title', item.type === 'manual' ? item.reference : item.title || item.reference);
    details.appendChild(title);

    if (item.type === 'product' && item.sku) {
      details.appendChild(makeElement('span', 'rfq-item__meta', builder.dataset.partLabel + ': ' + item.sku));
    }

    if (item.type === 'product' && item.variantTitle && item.variantTitle.toLowerCase() !== 'default title') {
      details.appendChild(makeElement('span', 'rfq-item__meta', item.variantTitle));
    }

    if (item.url) {
      var productLink = makeElement('a', 'rfq-item__link link link--accented', builder.dataset.viewProductLabel);
      productLink.href = item.url;
      details.appendChild(productLink);
    }

    itemElement.appendChild(details);
    appendField(itemElement, item, builder, 'quantity', index);
    appendField(itemElement, item, builder, 'condition', index);

    var removeButton = makeElement('button', 'rfq-item__remove link link--accented', builder.dataset.removeLabel);
    removeButton.type = 'button';
    removeButton.setAttribute('data-rfq-remove-item', '');
    removeButton.setAttribute('aria-label', builder.dataset.removeLabel + ': ' + (item.title || item.reference));
    itemElement.appendChild(removeButton);

    return itemElement;
  }

  function serializeItems(items) {
    if (!items.length) return '';

    var lines = ['FOUR NORDIC QUOTE REQUEST', 'Line items: ' + items.length, ''];

    items.forEach(function (item, index) {
      lines.push((index + 1) + '. ' + (item.type === 'manual' ? 'Manual part request' : 'Website product'));
      if (item.sku || item.reference) lines.push('Part number: ' + (item.sku || item.reference));
      if (item.type === 'product' && item.title) lines.push('Product: ' + item.title);
      if (item.variantTitle && item.variantTitle.toLowerCase() !== 'default title') lines.push('Variant: ' + item.variantTitle);
      lines.push('Quantity: ' + item.quantity);
      lines.push('Preferred condition: ' + conditionValueForEmail(item.condition));
      if (item.url) lines.push('Product URL: ' + item.url);
      lines.push('');
    });

    return lines.join('\n').trim();
  }

  function renderBuilder(builder, items) {
    var container = builder.querySelector('[data-rfq-items]');
    var emptyState = builder.querySelector('[data-rfq-empty]');
    var payload = builder.querySelector('[data-rfq-payload]');
    var itemCount = builder.querySelector('[data-rfq-item-count]');
    if (!container || !emptyState || !payload || !itemCount) return;

    while (container.firstChild) container.removeChild(container.firstChild);
    items.forEach(function (item, index) {
      container.appendChild(renderItem(item, builder, index));
    });

    emptyState.hidden = items.length > 0;
    payload.value = serializeItems(items);
    itemCount.value = String(items.length);
  }

  function renderInterface(items) {
    document.querySelectorAll('[data-rfq-count]').forEach(function (count) {
      count.textContent = String(items.length);
      count.hidden = items.length === 0;
    });

    document.querySelectorAll('[data-rfq-builder]').forEach(function (builder) {
      renderBuilder(builder, items);
    });
  }

  function showToast(message) {
    var toast = document.querySelector('[data-rfq-toast]');
    if (!toast || !message) return;

    window.clearTimeout(toast._rfqTimer);
    toast.textContent = message;
    toast.classList.add('is-visible');
    toast._rfqTimer = window.setTimeout(function () {
      toast.classList.remove('is-visible');
    }, 2600);
  }

  function addProduct(button) {
    var productId = cleanText(button.dataset.rfqProductId, 50);
    var variantId = cleanText(button.dataset.rfqVariantId, 50);
    if (!productId && !variantId) return;

    var quantityInput = button.closest('form') ? button.closest('form').querySelector('[name="quantity"]') : null;
    var quantity = cleanQuantity(quantityInput ? quantityInput.value : 1);
    var itemId = 'product:' + productId + ':' + variantId;
    var items = readItems();
    var existingItem = items.find(function (item) { return item.id === itemId; });
    var itemAdded = false;

    if (existingItem) {
      existingItem.quantity = cleanQuantity(existingItem.quantity + quantity);
      itemAdded = true;
    } else if (items.length < MAX_ITEMS) {
      items.push(normalizeItem({
        id: itemId,
        type: 'product',
        productId: productId,
        variantId: variantId,
        title: button.dataset.rfqProductTitle,
        variantTitle: button.dataset.rfqVariantTitle,
        sku: button.dataset.rfqSku,
        reference: button.dataset.rfqSku || button.dataset.rfqProductTitle,
        url: button.dataset.rfqProductUrl,
        quantity: quantity,
        condition: 'any'
      }));
      itemAdded = true;
    }

    if (!itemAdded) {
      showToast(button.dataset.rfqLimitLabel);
      return;
    }

    saveItems(items);
    trackEvent('rfq_add', {
      item_type: 'product',
      product_id: productId,
      variant_id: variantId,
      quantity: quantity,
      item_count: items.length
    });
    showToast(button.dataset.rfqAddedLabel);

    var label = button.querySelector('[data-rfq-button-label]');
    if (label) {
      window.clearTimeout(button._rfqLabelTimer);
      label.textContent = button.dataset.rfqAddedLabel;
      button._rfqLabelTimer = window.setTimeout(function () {
        label.textContent = button.dataset.rfqAddLabel;
      }, 1800);
    }
  }

  function addManualPart(builder, showValidation) {
    var partInput = builder.querySelector('[data-rfq-manual-part]');
    var quantityInput = builder.querySelector('[data-rfq-manual-quantity]');
    var conditionInput = builder.querySelector('[data-rfq-manual-condition]');
    var errorElement = builder.querySelector('[data-rfq-manual-error]');
    var reference = cleanText(partInput ? partInput.value : '', 200);

    if (!reference) {
      if (showValidation && errorElement) {
        errorElement.textContent = builder.dataset.partRequiredMessage;
        errorElement.hidden = false;
      }
      if (showValidation && partInput) partInput.focus();
      return false;
    }

    if (errorElement) errorElement.hidden = true;

    var quantity = cleanQuantity(quantityInput ? quantityInput.value : 1);
    var condition = cleanCondition(conditionInput ? conditionInput.value : 'any');
    var itemId = 'manual:' + reference.toLowerCase();
    var items = readItems();
    var existingItem = items.find(function (item) { return item.id === itemId; });
    var itemAdded = false;

    if (existingItem) {
      existingItem.quantity = cleanQuantity(existingItem.quantity + quantity);
      existingItem.condition = condition;
      itemAdded = true;
    } else if (items.length < MAX_ITEMS) {
      items.push(normalizeItem({
        id: itemId,
        type: 'manual',
        reference: reference,
        title: reference,
        quantity: quantity,
        condition: condition
      }));
      itemAdded = true;
    }

    if (!itemAdded) {
      if (errorElement) {
        errorElement.textContent = builder.dataset.limitMessage;
        errorElement.hidden = false;
      }
      return false;
    }

    saveItems(items);
    trackEvent('rfq_add', {
      item_type: 'manual',
      quantity: quantity,
      item_count: items.length
    });
    if (partInput) partInput.value = '';
    if (quantityInput) quantityInput.value = '1';
    if (conditionInput) conditionInput.value = 'any';
    return true;
  }

  function updateItemField(target) {
    var itemElement = target.closest('[data-rfq-item-id]');
    if (!itemElement) return;

    var items = readItems();
    var item = items.find(function (candidate) { return candidate.id === itemElement.dataset.rfqItemId; });
    if (!item) return;

    if (target.hasAttribute('data-rfq-item-quantity')) item.quantity = cleanQuantity(target.value);
    if (target.hasAttribute('data-rfq-item-condition')) item.condition = cleanCondition(target.value);
    saveItems(items);
  }

  function removeItem(button) {
    var itemElement = button.closest('[data-rfq-item-id]');
    if (!itemElement) return;
    var items = readItems();
    var removedItem = items.find(function (item) { return item.id === itemElement.dataset.rfqItemId; });
    var remainingItems = items.filter(function (item) { return item.id !== itemElement.dataset.rfqItemId; });
    saveItems(remainingItems);
    trackEvent('rfq_remove', {
      item_type: removedItem ? removedItem.type : 'unknown',
      item_count: remainingItems.length
    });
  }

  function importPartFromUrl() {
    var builder = document.querySelector('[data-rfq-builder]');
    if (!builder) return;

    var params = new URLSearchParams(window.location.search);
    var reference = cleanText(params.get('part'), 200);
    if (!reference) return;

    var items = readItems();
    var referenceLower = reference.toLowerCase();
    var alreadyPresent = items.some(function (item) {
      return cleanText(item.sku || item.reference, 200).toLowerCase() === referenceLower;
    });

    if (!alreadyPresent && items.length < MAX_ITEMS) {
      items.push(normalizeItem({
        id: 'manual:' + referenceLower,
        type: 'manual',
        reference: reference,
        title: reference,
        quantity: cleanQuantity(params.get('quantity')),
        condition: cleanCondition(params.get('condition'))
      }));
      saveItems(items);
    }
  }

  function updateVariantButton(event) {
    var variant = event.detail && event.detail.variant;
    if (!variant || !variant.id) return;

    var productForm = event.target && event.target.closest ? event.target.closest('form[action*="/cart/add"]') : null;
    var scope = productForm || (event.target && event.target.closest ? event.target.closest('.product-form') : null) || document;

    scope.querySelectorAll('[data-rfq-add-product]').forEach(function (button) {
      button.dataset.rfqVariantId = String(variant.id);
      button.dataset.rfqVariantTitle = cleanText(variant.title, 200);
      button.dataset.rfqSku = cleanText(variant.sku, 200);

      try {
        var productUrl = new URL(button.dataset.rfqProductUrl || window.location.href, window.location.origin);
        productUrl.searchParams.set('variant', variant.id);
        button.dataset.rfqProductUrl = productUrl.href;
      } catch (error) {
        // Keep the initial product URL if the browser cannot parse it.
      }
    });
  }

  function initialize() {
    if (document.querySelector('[data-rfq-submit-success]')) {
      saveItems([]);
    } else {
      memoryItems = readItems();
      renderInterface(memoryItems);
      importPartFromUrl();
    }
  }

  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;

    var productButton = event.target.closest('[data-rfq-add-product]');
    if (productButton) {
      event.preventDefault();
      addProduct(productButton);
      return;
    }

    var manualButton = event.target.closest('[data-rfq-add-manual]');
    if (manualButton) {
      addManualPart(manualButton.closest('[data-rfq-builder]'), true);
      return;
    }

    var removeButton = event.target.closest('[data-rfq-remove-item]');
    if (removeButton) removeItem(removeButton);
  });

  document.addEventListener('change', function (event) {
    if (event.target.matches('[data-rfq-item-quantity], [data-rfq-item-condition]')) updateItemField(event.target);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Enter' && event.target.matches('[data-rfq-manual-part]')) {
      event.preventDefault();
      addManualPart(event.target.closest('[data-rfq-builder]'), true);
    }
  });

  document.addEventListener('submit', function (event) {
    var builder = event.target.querySelector && event.target.querySelector('[data-rfq-builder]');
    if (!builder) return;

    var partInput = builder.querySelector('[data-rfq-manual-part]');
    if (partInput && cleanText(partInput.value, 200)) addManualPart(builder, false);
    var items = readItems();
    renderBuilder(builder, items);
    trackEvent('generate_lead', {
      lead_source: 'rfq',
      item_count: items.length
    });
  }, true);

  document.addEventListener('variant:changed', updateVariantButton);
  document.addEventListener('shopify:section:load', function () { renderInterface(readItems()); });
  window.addEventListener('storage', function (event) {
    if (event.key === STORAGE_KEY) renderInterface(readItems());
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }
})();
